import {
  BookmarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  HeartIcon,
  MessageCircleIcon,
  SendIcon,
} from 'lucide-react'
import { useState } from 'react'
import { Boton } from '@/components/ui/Boton'
import { cn } from '@/lib/cn'
import { MediaSlot } from './MediaSlot'
import {
  ETIQUETA_RED,
  type RedSocial,
  type TipoPublicacion,
} from '@/lib/schemas/publicaciones'

interface VistaPreviaProps {
  /** El @ del cliente: se carga en Administración. */
  usuarioInstagram: string
  tipo: TipoPublicacion
  copy: string
  /** Archivos de Drive pegados, en orden. */
  archivos: string[]
  /** Portada del video, si ya se pegó. */
  portada?: string | undefined
  pilares: string[]
  redes: RedSocial[]
}

const ALTO = '60vh'

/**
 * "Así se va a ver" (§8.5). La vista previa es siempre de Instagram, sea cual
 * sea la red elegida. El contenido es la miniatura de Drive de lo que se
 * eligió (la portada, en un video); mientras no hay nada, el degradado
 * reserva el espacio, igual que en la grilla.
 *
 * Como en el panel (revisión OO del 23/09/2026): el aspecto de Instagram
 * —cabecera con el @ del cliente, íconos, copy con el @— sin la tarjeta con
 * borde que lo envolvía y achicaba la pieza.
 */
export function VistaPrevia({
  usuarioInstagram,
  tipo,
  copy,
  archivos,
  portada,
  pilares,
  redes,
}: VistaPreviaProps) {
  const [lamina, setLamina] = useState(0)
  // Instagram arma todo el carrusel con la proporción de la primera lámina.
  const [proporcion, setProporcion] = useState<number | undefined>(undefined)
  // Si se borran láminas, la que se estaba mirando puede dejar de existir.
  const actual = Math.min(lamina, Math.max(archivos.length - 1, 0))
  const archivo = tipo === 'video' ? portada : archivos[actual]
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm font-medium">Así se va a ver</p>
      {/* El posteo toma el ancho de la pieza. */}
      <article
        className="mx-auto flex w-full flex-col gap-2"
        style={
          proporcion
            ? { width: `min(100%, calc(${ALTO} * ${proporcion.toFixed(4)}))` }
            : undefined
        }
      >
        <header className="flex items-center gap-2.5">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-borde text-xs font-semibold">
            {usuarioInstagram.slice(0, 2).toUpperCase()}
          </span>
          <span className="min-w-0 flex-1 truncate text-sm font-semibold">
            @{usuarioInstagram}
          </span>
        </header>

        <div className="relative overflow-hidden rounded-lg">
          <MediaSlot
            aspect="archivo"
            altoMaximo={ALTO}
            proporcion={tipo === 'carrusel' && actual > 0 ? proporcion : undefined}
            alMedir={(r) => {
              if (tipo !== 'carrusel' || actual === 0) setProporcion(r)
            }}
            driveFileId={archivo}
            titulo={tipo === 'carrusel' ? `Lámina ${String(actual + 1)}` : undefined}
          />
          {tipo === 'carrusel' && archivos.length > 0 && (
            <span className="absolute top-3 right-3 rounded-full bg-tinta/70 px-2.5 py-1 text-xs font-medium text-white">
              {actual + 1} / {archivos.length}
            </span>
          )}
          {tipo === 'carrusel' && archivos.length > 1 && (
            <>
              <Boton
                variante="secundario"
                tamano="icono"
                aria-label="Lámina anterior"
                disabled={actual === 0}
                onClick={() => {
                  setLamina(actual - 1)
                }}
                className="absolute top-1/2 left-2 size-9 -translate-y-1/2 bg-tarjeta/90"
              >
                <ChevronLeftIcon className="size-5" aria-hidden />
              </Boton>
              <Boton
                variante="secundario"
                tamano="icono"
                aria-label="Lámina siguiente"
                disabled={actual === archivos.length - 1}
                onClick={() => {
                  setLamina(actual + 1)
                }}
                className="absolute top-1/2 right-2 size-9 -translate-y-1/2 bg-tarjeta/90"
              >
                <ChevronRightIcon className="size-5" aria-hidden />
              </Boton>
            </>
          )}
        </div>

        {/* Íconos de Instagram, con los puntos del carrusel en el medio. */}
        <div
          className="grid grid-cols-[1fr_auto_1fr] items-center text-tinta-suave"
          aria-hidden
        >
          <div className="flex items-center gap-3.5">
            <HeartIcon className="size-5" />
            <MessageCircleIcon className="size-5" />
            <SendIcon className="size-5" />
          </div>
          <div className="flex justify-center gap-1.5">
            {tipo === 'carrusel' &&
              archivos.length > 1 &&
              archivos.map((a, i) => (
                <span
                  key={a}
                  className={cn(
                    'size-1.5 rounded-full',
                    i === actual ? 'bg-tinta' : 'bg-borde',
                  )}
                />
              ))}
          </div>
          <BookmarkIcon className="size-5 justify-self-end" />
        </div>

        <p className="text-sm whitespace-pre-wrap">
          <span className="font-semibold">@{usuarioInstagram}</span>{' '}
          {copy || (
            <span className="text-tinta-suave">Todavía no escribiste el copy.</span>
          )}
        </p>
      </article>

      <dl className="space-y-0.5 text-xs text-tinta-suave">
        <div className="flex gap-1.5">
          <dt>Red:</dt>
          <dd>{redes.map((r) => ETIQUETA_RED[r]).join(', ') || '—'}</dd>
        </div>
        <div className="flex gap-1.5">
          <dt>Pilares:</dt>
          <dd>{pilares.join(', ') || '—'}</dd>
        </div>
      </dl>
    </div>
  )
}
